import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DateService } from 'common/services/date/date.service';
import { createAccountEmailInputValidator, createAccountInputValidator } from 'common/utils/createAccountValidators';
import { EmailVerification } from 'private/app/components/email-validation/email-validation.component';
import { UserValidationService } from 'private/app/services/user-validation/user-validation.service';
import { ProfileDetails } from 'private/app/models/user-validation';
import { Option } from 'common/components/select/select.component';
import { map, mergeMap, takeUntil, filter } from 'rxjs/operators';
import { GetUserStatus } from 'common/store/account-status/account-status.actions';
import { ToastService } from 'common/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { AccountStatusModel } from 'common/models/account-status';
import { AccountStatusState } from 'common/store/account-status/account-status.state';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CountryForMasking } from 'private/app/services/account-admin/account-admin-company-create.service';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { AppConstants } from 'common/app-constants';
import { inputValidator } from 'private/app/services/account-admin/account-admin-edit-form.service';
import { PlaceHolderText } from 'private/app/models/account-admin-company.model';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ProfileComponent implements OnInit {
    @Select(AccountStatusState.getProfileInfo) userInfo$: Observable<AccountStatusModel>;
    ngOnDestroy$ = new Subject();

    placeHolderByCountry$: BehaviorSubject<PlaceHolderText> = new BehaviorSubject<PlaceHolderText>({
        placeHolderPhone: '',
        placeHolderOffice: '',
        placeHolderMobile: '',
        placeHolderFax: '',
        placeHolderTollFree: '',
        placeHolderZipcode: { label: '' }
    });

    public profileForm: UntypedFormGroup = new UntypedFormGroup({
        address1: new UntypedFormControl('', [createAccountInputValidator(61, true)]),
        address2: new UntypedFormControl('', [createAccountInputValidator(61, false)]),
        cellPhone: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        city: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        country: new UntypedFormControl(''),
        email: new UntypedFormControl('', [createAccountEmailInputValidator(242, true, AppConstants.validEmailRegex)]),
        fax: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        firstName: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        lastName: new UntypedFormControl('', [createAccountInputValidator(41, true)]),
        phone: new UntypedFormControl('', [inputValidator(21, false), Validators.pattern(AppConstants.validPhoneRegex)]),
        state: new UntypedFormControl('', [Validators.required]),
        userName: new UntypedFormControl({
            value: '',
            disabled: true
        }),
        zip: new UntypedFormControl('', [Validators.required]),
        myHVACpin: new UntypedFormControl({
            value: '',
            disabled: true
        })
    });

    enableEmailVerification$ = new BehaviorSubject<boolean>(false);
    countryOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    stateOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    bannerType = 'ProfileBanner';
    public apiRequestActive: boolean;
    public maskPhoneNumber: boolean = false;
    public stateListLoading = false;
    private userProfile$: BehaviorSubject<ProfileDetails[]> = new BehaviorSubject<ProfileDetails[]>([]);
    private userEmailId$ = new BehaviorSubject<string>('');
    private emailVerificationStatus = new BehaviorSubject<EmailVerification>({
        email: '',
        status: false
    });

    constructor(
        private store: Store,
        public dateService: DateService,
        private userValidationService: UserValidationService,
        private toastService: ToastService,
        private translate: TranslateService,
        private commonUtils: CommonUtilsService,
        public userAdminService: UserAdminService,
        public accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService
    ) { }

    ngOnInit(): void {
        this.userInfo$.pipe(
            takeUntil(this.ngOnDestroy$),
            filter((statusData) => Boolean(statusData?.userId)),
            mergeMap((statusData) => forkJoin([this.userValidationService.getProfileDetailsV1(statusData?.userId)]))
        ).subscribe((profileInfo) => {
            this.userEmailId$.next(profileInfo[0].email);
            this.userProfile$.next(profileInfo);
            this.setFormValues(profileInfo[0]);
        }, (error: HttpErrorResponse) => this.toastService.raiseDefaultErrorToast('ProfileComponent-ngOnInit', error.message));

        this.commonUtils.getCountryOptions().subscribe((countryList) => {
            this.countryOptionData$.next(countryList);
        });
        this.accountAdminCompanyUtilsService.placeHolderData$.subscribe((placeHolderData) => {
            this.placeHolderByCountry$.next(placeHolderData);
        });
        this.profileForm.controls.country.valueChanges.subscribe((selectedCountry) => {
            if (selectedCountry) {
                if (Array.isArray(selectedCountry)) {
                    this.maskPhoneNumber = Boolean(selectedCountry[0].value === CountryForMasking.US || selectedCountry[0].value === CountryForMasking.CA);
                    this.stateOptionData$.next([]);
                    this.fetchStateListByCountry(selectedCountry[0].value);
                    this.accountAdminCompanyUtilsService.formInputPlaceHolder(selectedCountry[0].value);
                    this.userValidationService.updatePostalCodeValidator(this.profileForm, selectedCountry[0].value, 'zip');
                    this.commonUtils.updatePhoneValidators(this.profileForm, selectedCountry[0].value, ['phone', 'cellPhone', 'fax']);
                }
                else {
                    const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);
                    this.maskPhoneNumber = countrySelection?.value === CountryForMasking.US || countrySelection?.value === CountryForMasking.CA;
                    if (countrySelection) {
                        this.accountAdminCompanyUtilsService.formInputPlaceHolder(countrySelection.value);
                        this.fetchStateListByCountry(countrySelection.value);
                        this.userValidationService.updatePostalCodeValidator(this.profileForm, countrySelection.value, 'zip');
                        this.commonUtils.updatePhoneValidators(this.profileForm, countrySelection.value, ['phone', 'cellPhone', 'fax']);
                    }
                }
                this.profileForm.controls.state.setValue('');
                this.profileForm.controls.state.markAsTouched();
                this.profileForm.controls.zip.setValue('');
            }
        });


        combineLatest([
            this.userProfile$,
            this.countryOptionData$
        ]).pipe(
            map(([profileInfo, countryOptionData$]) => {
                if (profileInfo.length > 0 && countryOptionData$.length > 0) {
                    return this.setCountrySelection(profileInfo);
                }
            })
        ).subscribe();

        combineLatest([
            this.userProfile$,
            this.countryOptionData$,
            this.stateOptionData$
        ]).pipe(
            map(([profileInfo, countryOptionData, stateOptionData]) => {
                if (profileInfo.length > 0 && countryOptionData.length > 0 && stateOptionData.length > 0) {
                    return this.setStateSelection(profileInfo, stateOptionData);
                }
            })
        ).subscribe();
    }

    fetchStateListByCountry(selectedCountry: string) {
        this.stateListLoading = true;
        this.commonUtils.getStateByCountry(selectedCountry).subscribe({
            next: (stateList) => {
                this.stateOptionData$.next(stateList);
                this.stateListLoading = false;
            }
        });
    }

    emailVerification(emailVerification: EmailVerification) {
        if (emailVerification.status) {
            this.emailVerificationStatus.next(emailVerification);
            this.enableEmailVerification$.next(false);
            this.updateProfileInfo();
        }
    }

    setCountrySelection(profileInfo: ProfileDetails[]) {
        const countrySelection = this.countryOptionData$.value.find((country) => country.value === profileInfo[0].userCountry);
        if (countrySelection) {
            this.profileForm.controls.country.setValue(countrySelection.name);
        }
    }

    setStateSelection(profileInfo: ProfileDetails[], stateOptionData: Option[]) {
        if (this.profileForm.controls.state.value) {
            return;
        }
        const stateSelection = stateOptionData.find((country) => country.value === profileInfo[0].userState);
        if (stateSelection) {
            this.profileForm.controls.state.setValue(stateSelection.name);
        }
    }

    onPhoneInput(controlName: string) {
        const control = this.userAdminService.userEditForm.controls[controlName];
        if (!control?.value) {
            return;
        }
        control.setValue(control.value.replace(/\D/g, ''), { emitEvent: false });
    }

    public getPhonePlaceholder(fieldType: string): string {
        const countryCode = this.getSelectedCountryCode();

        return this.commonUtils.getPhonePlaceholder(fieldType, countryCode);
    }

    updateProfileInfo() {
        if (!(this.userEmailId$.value.toLowerCase() === this.profileForm.controls.email.value.toLowerCase()) && this.emailVerificationStatus.value.email.toLowerCase() !== this.profileForm.controls.email.value.toLowerCase()) {
            this.enableEmailVerification$.next(true);

            return;
        }

        this.apiRequestActive = true;
        this.setUpdateUserProfileData();
        this.userValidationService.updateUserProfileDetailsV1(this.userProfile$.value[0]).subscribe((data) => {
            this.apiRequestActive = false;
            if (data.status === 'Success') {
                this.toastService.add({
                    content: this.translate.instant('USER_VALIDATION.PROFILE_UPDATE_SUCCESS'),
                    theme: 'success',
                    id: this.bannerType,
                    outletName: this.bannerType,
                    closeable: true,
                    autoClose: true
                });
                this.enableEmailVerification$.next(false);
                this.store.dispatch(new GetUserStatus());
            }
        });
    }

    setUpdateUserProfileData() {
        this.userProfile$.value[0].firstName = this.profileForm.controls.firstName.value;
        this.userProfile$.value[0].lastName = this.profileForm.controls.lastName.value;
        this.userProfile$.value[0].userAddress1 = this.profileForm.controls.address1.value;
        this.userProfile$.value[0].userAddress2 = this.profileForm.controls.address2.value;
        this.userProfile$.value[0].userCity = this.profileForm.controls.city.value;
        this.userProfile$.value[0].email = this.profileForm.controls.email.value;
        this.userProfile$.value[0].userPostalCode = this.profileForm.controls.zip.value;
        this.userProfile$.value[0].userPhone = this.profileForm.controls.phone.value.replace(/\D/g, '');
        this.userProfile$.value[0].userCellPhone = this.profileForm.controls.cellPhone.value.replace(/\D/g, '');
        this.userProfile$.value[0].userFax = this.profileForm.controls.fax.value.replace(/\D/g, '');
        if (Array.isArray(this.profileForm.controls.country.value)) {
            this.userProfile$.value[0].userCountry = this.countryOptionData$.value.find((country) => country.name === this.profileForm.controls.country.value[0].name)?.value || '';
        }
        else {
            this.userProfile$.value[0].userCountry = this.countryOptionData$.value.find((country) => country.name.toLowerCase() === this.profileForm.controls.country.value.toLowerCase())?.value || '';
        }

        if (Array.isArray(this.profileForm.controls.state.value)) {
            this.userProfile$.value[0].userState = this.stateOptionData$.value.find((state) => state.name === this.profileForm.controls.state.value[0].name)?.value || '';
        }
        else {
            this.userProfile$.value[0].userState = this.stateOptionData$.value.find((state) => state.name === this.profileForm.controls.state.value)?.value || '';
        }
    }

    cancelValidation(value: boolean) {
        this.enableEmailVerification$.next(value);
    }

    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    onPostalCodeInput(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!this.getSelectedCountryCode()) {
            return;
        }
        const formattedValue = this.commonUtils.formatPostalCode(input.value, this.getSelectedCountryCode());
        input.value = formattedValue;
        this.profileForm.controls.zip.setValue(formattedValue, { emitEvent: false });
    }

    private formatForMask(val: string): string {
        const digits = val.replace(/\D/g, '').substring(0, 10);
        const x = digits.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        if (!x) {
            return digits;
        }

        return x[2] ? `(${x[1]}) ${x[2]}${x[3] ? ` ${x[3]}` : ''}` : x[1];
    }

    private setFormValues(user: ProfileDetails) {
        if (user) {
            this.maskPhoneNumber = user.userCountry === CountryForMasking.US || user.userCountry === CountryForMasking.CA;
            this.profileForm.controls.address1.setValue(user.userAddress1 || '');
            this.profileForm.controls.address2.setValue(user.userAddress2 || '');
            this.profileForm.controls.city.setValue(user.userCity || '');
            this.profileForm.controls.email.setValue(user.email || '');
            this.profileForm.controls.firstName.setValue(user.firstName || '');
            this.profileForm.controls.lastName.setValue(user.lastName || '');
            this.profileForm.controls.userName.setValue(user.userID || '');
            this.profileForm.controls.zip.setValue(user.userPostalCode || '');
            setTimeout(() => {
                const phone = this.maskPhoneNumber ? this.formatForMask(user.userPhone || '') : (user.userPhone || '');
                const cellPhone = this.maskPhoneNumber ? this.formatForMask(user.userCellPhone || '') : (user.userCellPhone || '');
                const fax = this.maskPhoneNumber ? this.formatForMask(user.userFax || '') : (user.userFax || '');

                this.profileForm.controls.phone.setValue(phone);
                this.profileForm.controls.cellPhone.setValue(cellPhone);
                this.profileForm.controls.fax.setValue(fax);
            });
            this.profileForm.controls.myHVACpin.setValue(user.hvacPin || '');
            this.profileForm.markAllAsTouched();
            this.profileForm.controls.country.markAsUntouched();
            this.profileForm.controls.state.markAsUntouched();
        }
    }

    private getSelectedCountryCode(): string {
        const selectedCountry = this.profileForm.controls.country.value;
        if (Array.isArray(selectedCountry)) {
            return selectedCountry[0]?.value || '';
        }
        const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);

        return countrySelection?.value || '';
    }
}
